// Standalone consent-form page accessible at /consent/:workflowId
// Reuses patterns from ReviewAndSign.tsx but fetches from the patient-consent API.
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  memo,
} from 'react';
import { useParams } from 'react-router-dom';
import AppButton from '../components/AppButton';
import SignatureCanvas, {
  SignatureCanvasHandle,
} from '../components/SignatureCanvas';
import { useDispatch } from 'react-redux';
import { Alert } from '../../components/shared/Alert';
import axiosInstance from '../../utils/axiosConfig';
import { getApiErrorCode, getApiErrorMessage } from '../../utils/apiError';
import { UUID_REGEX } from '../../utils/constants';
import { hideError } from '../../store/errorSlice';
import DOMPurify from 'dompurify';
import { getFieldLabel, getGroupLabel } from '../utils/formLabel';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ConsentForm {
  id: string;
  name: string;
  htmlContent: string;
  signedByPatientText: string;
  signedOnBehalfOfPatientText: string;
  displayOrder: number;
}

interface ConsentFormsApiResponse {
  status: number;
  message: string;
  data: {
    consentForms: ConsentForm[];
    allStepsCompleted: boolean | null;
    workflowStatus: string | null;
  };
  success: boolean;
}

/* ------------------------------------------------------------------ */
/*  Form-element parsing (checkboxes / radios inside htmlContent)      */
/* ------------------------------------------------------------------ */

interface FormElementInfo {
  identifier: string;
  isRequired: boolean;
  type: 'checkbox' | 'radio';
  value?: string;
  groupName?: string;
}

interface ParsedFormElements {
  checkboxes: FormElementInfo[];
  radioGroups: Map<string, FormElementInfo[]>;
  mandatoryCheckboxes: string[];
  mandatoryRadioGroups: string[];
  mandatoryCheckboxGroups: Map<string, string[]>;
  fieldLabels: Map<string, string>;
  groupLabels: Map<string, string>;
}

function parseFormElements(htmlContent: string): ParsedFormElements {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  // checkboxes
  const checkboxes: FormElementInfo[] = [];
  const mandatoryCheckboxes: string[] = [];
  const mandatoryCheckboxGroups = new Map<string, string[]>();
  const fieldLabels = new Map<string, string>();
  const groupLabels = new Map<string, string>();

  doc.querySelectorAll('input[type="checkbox"]').forEach((el) => {
    const cb = el as HTMLInputElement;
    const identifier = cb.getAttribute('name') || cb.getAttribute('id');
    if (!identifier) return;
    const isRequired = cb.hasAttribute('required');
    const requiredGroup = cb.getAttribute('data-required-group');
    checkboxes.push({ identifier, isRequired, type: 'checkbox' });
    fieldLabels.set(identifier, getFieldLabel(cb, identifier));
    if (isRequired) mandatoryCheckboxes.push(identifier);
    if (requiredGroup) {
      if (!mandatoryCheckboxGroups.has(requiredGroup)) {
        mandatoryCheckboxGroups.set(requiredGroup, []);
      }
      mandatoryCheckboxGroups.get(requiredGroup)!.push(identifier);
      if (!groupLabels.has(requiredGroup)) {
        groupLabels.set(requiredGroup, getGroupLabel(doc, requiredGroup));
      }
    }
  });

  // radios
  const radioGroups = new Map<string, FormElementInfo[]>();
  const mandatoryRadioGroups: string[] = [];
  const processedGroups = new Set<string>();
  doc.querySelectorAll('input[type="radio"]').forEach((el) => {
    const radio = el as HTMLInputElement;
    const groupName = radio.getAttribute('name');
    if (!groupName) return;
    const value = radio.getAttribute('value') || '';
    const isRequired = radio.hasAttribute('required');
    const identifier = radio.getAttribute('id') || `${groupName}_${value}`;
    if (!radioGroups.has(groupName)) radioGroups.set(groupName, []);
    radioGroups.get(groupName)!.push({ identifier, isRequired, type: 'radio', value, groupName });
    if (isRequired && !processedGroups.has(groupName)) {
      mandatoryRadioGroups.push(groupName);
      processedGroups.add(groupName);
    }
  });

  return { checkboxes, radioGroups, mandatoryCheckboxes, mandatoryRadioGroups, mandatoryCheckboxGroups, fieldLabels, groupLabels };
}

function mergeFormElements(a: ParsedFormElements, b: ParsedFormElements): ParsedFormElements {
  const radioGroups = new Map<string, FormElementInfo[]>();
  a.radioGroups.forEach((v, k) => radioGroups.set(k, v));
  b.radioGroups.forEach((v, k) => {
    const existing = radioGroups.get(k) || [];
    radioGroups.set(k, [...existing, ...v]);
  });
  const mandatoryCheckboxGroups = new Map<string, string[]>();
  a.mandatoryCheckboxGroups.forEach((ids, groupName) => {
    mandatoryCheckboxGroups.set(groupName, [...ids]);
  });
  b.mandatoryCheckboxGroups.forEach((ids, groupName) => {
    const existing = mandatoryCheckboxGroups.get(groupName) || [];
    mandatoryCheckboxGroups.set(groupName, [...existing, ...ids]);
  });
  return {
    checkboxes: [...a.checkboxes, ...b.checkboxes],
    radioGroups,
    mandatoryCheckboxes: [...a.mandatoryCheckboxes, ...b.mandatoryCheckboxes],
    mandatoryRadioGroups: [...a.mandatoryRadioGroups, ...b.mandatoryRadioGroups],
    mandatoryCheckboxGroups,
    fieldLabels: (() => { const m = new Map(a.fieldLabels); b.fieldLabels.forEach((v, k) => m.set(k, v)); return m; })(),
    groupLabels: (() => { const m = new Map(a.groupLabels); b.groupLabels.forEach((v, k) => m.set(k, v)); return m; })(),
  };
}

/* ------------------------------------------------------------------ */
/*  ConsentFormItem (memoised per-form card)                           */
/* ------------------------------------------------------------------ */

interface ConsentFormItemProps {
  form: ConsentForm;
  isOpen: boolean;
  isSigned: boolean;
  signerType: 'patient' | 'proxy';
  formValidationErrors?: string[];
  signingForms: Set<string>;
  onToggleOpen: () => void;
  onSign: (formId: string) => void;
  contentRef: (el: HTMLDivElement | null) => void;
  signingTextRef: (el: HTMLDivElement | null) => void;
  signatureRef: (el: SignatureCanvasHandle | null) => void;
  errorAnchorRef: (el: HTMLDivElement | null) => void;
}

const ConsentFormItem = memo<ConsentFormItemProps>(
  ({
    form,
    isOpen,
    isSigned,
    signerType,
    formValidationErrors,
    signingForms,
    onToggleOpen,
    onSign,
    contentRef,
    signingTextRef,
    signatureRef,
    errorAnchorRef,
  }) => {
    const signingText =
      signerType === 'patient'
        ? form.signedByPatientText
        : form.signedOnBehalfOfPatientText;

    return (
      <div
        className={`border rounded-xl overflow-hidden ${
          isOpen ? 'border-[#01545D]' : 'border-[#A4BDC0]'
        }`}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#DCE1E8] bg-white">
          <div className="flex items-center gap-3">
            <span className="text-base font-semibold text-[#1C1C1C]">
              {form.name}
            </span>
            {isSigned && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold bg-[#22C55E] text-white">
                Signed
              </span>
            )}
          </div>
          <button
            type="button"
            className="text-sm font-bold text-[#01545D] cursor-pointer"
            onClick={onToggleOpen}
          >
            {isOpen ? 'Close' : 'Read'}
          </button>
        </div>

        {/* Expanded body */}
        {isOpen && (
          <div className="bg-[#F7F8FD] px-3 py-4 sm:px-6 sm:py-5 flex flex-col gap-4">
            <div ref={errorAnchorRef} className="error-anchor" />
            {/* Validation errors */}
            {formValidationErrors && formValidationErrors.length > 0 && (
              <div className="bg-[#FEE] border border-[#FCC] rounded-lg px-4 py-3">
                <h3 className="text-sm font-semibold text-[#C00] mb-2">
                  Please fix the following errors:
                </h3>
                <ul className="list-disc list-inside text-sm text-[#C00] space-y-1">
                  {formValidationErrors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* HTML form content */}
            <div
              ref={contentRef}
              className="bg-white border border-[#DCE1E8] rounded-lg px-3 py-4 sm:px-5 sm:py-5 overflow-x-auto consent-form-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.htmlContent) }}
            />

            {/* Signing text */}
            {signingText && (
              <div
                ref={signingTextRef}
                className="bg-white border border-[#DCE1E8] rounded-lg px-3 py-4 sm:px-5 sm:py-5 overflow-x-auto signing-section"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(signingText) }}
              />
            )}

            {/* Signature pad */}
            <SignatureCanvas ref={signatureRef} />

            {!isSigned && (
              <button
                type="button"
                onClick={() => onSign(form.id)}
                disabled={signingForms.has(form.id)}
                className="mt-2 px-4 py-2 bg-[#01545D] text-white rounded-md font-semibold text-sm hover:bg-[#0b7981] disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {signingForms.has(form.id) ? 'Signing...' : 'Sign Consent Form'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  },
  (prev, next) => {
    const formId = prev.form.id;
    const pErr = prev.formValidationErrors || [];
    const nErr = next.formValidationErrors || [];
    return (
      prev.form.id === next.form.id &&
      prev.form.name === next.form.name &&
      prev.form.htmlContent === next.form.htmlContent &&
      prev.form.signedByPatientText === next.form.signedByPatientText &&
      prev.form.signedOnBehalfOfPatientText === next.form.signedOnBehalfOfPatientText &&
      prev.isOpen === next.isOpen &&
      prev.isSigned === next.isSigned &&
      prev.signerType === next.signerType &&
      prev.signingForms.has(formId) === next.signingForms.has(formId) &&
      pErr.length === nErr.length &&
      pErr.every((e, i) => e === nErr[i])
    );
  },
);

ConsentFormItem.displayName = 'ConsentFormItem';

/* ------------------------------------------------------------------ */
/*  Reusable content component (accepts workflowId as prop)            */
/* ------------------------------------------------------------------ */

interface ConsentPageContentProps {
  workflowId: string;
  onComplete?: () => void;
}

export const ConsentPageContent: React.FC<ConsentPageContentProps> = ({ workflowId, onComplete }) => {
  const dispatch = useDispatch();

  // Data
  const [loading, setLoading] = useState(false);
  const [error, setErrorInternal] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState(0);
  const setError = useCallback((msg: string | null) => {
    setErrorInternal(msg);
    if (msg !== null) setErrorKey((k) => k + 1);
  }, []);
  const [workflowAlreadyCompleted, setWorkflowAlreadyCompleted] = useState(false);
  const [consentForms, setConsentForms] = useState<ConsentForm[]>([]);

  // UI
  const [signerType, setSignerType] = useState<'patient' | 'proxy'>('patient');
  const [openForm, setOpenForm] = useState<string | null>(null);
  const [signedForms, setSignedForms] = useState<Set<string>>(new Set());
  const [signingForms, setSigningForms] = useState<Set<string>>(new Set());

  // Form element state (checkboxes / radios parsed from HTML)
  const [parsedElements, setParsedElements] = useState<Record<string, ParsedFormElements>>({});
  const [checkboxStates, setCheckboxStates] = useState<Record<string, Record<string, boolean>>>({});
  const [radioSelections, setRadioSelections] = useState<Record<string, Record<string, string>>>({});
  const [formValidationErrors, setFormValidationErrors] = useState<Record<string, string[]>>({});

  // Refs
  const signatureRefs = useRef<Record<string, SignatureCanvasHandle>>({});
  const contentRefs = useRef<Record<string, HTMLDivElement>>({});
  const signingTextRefs = useRef<Record<string, HTMLDivElement>>({});
  const errorAnchorRefs = useRef<Record<string, HTMLDivElement>>({});
  const pageTopRef = useRef<HTMLDivElement>(null);
  const eventListenerCleanups = useRef<Record<string, Array<() => void>>>({});
  const processedPlaceholders = useRef<Record<string, boolean>>({});

  /* ---------------------- Fetch consent forms ---------------------- */

  useEffect(() => {
    if (!workflowId) {
      setError('Missing workflow id in URL.');
      return;
    }
    if (!UUID_REGEX.test(workflowId)) {
      setError('Invalid workflow id.');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setWorkflowAlreadyCompleted(false);

    axiosInstance
      .get<ConsentFormsApiResponse>(
        `/api/patient-consent/workflows/${encodeURIComponent(workflowId)}/consent-forms`,
      )
      .then((res) => {
        if (cancelled) return;
        const forms = (res.data?.data?.consentForms ?? []).sort(
          (a, b) => a.displayOrder - b.displayOrder,
        );
        setConsentForms(forms);

        // Parse interactive form elements for each form
        const newParsed: Record<string, ParsedFormElements> = {};
        const newCb: Record<string, Record<string, boolean>> = {};
        const newRadio: Record<string, Record<string, string>> = {};

        forms.forEach((form) => {
          const contentEls = parseFormElements(form.htmlContent);
          const signingEls = parseFormElements(
            signerType === 'patient'
              ? form.signedByPatientText
              : form.signedOnBehalfOfPatientText,
          );
          const merged = mergeFormElements(contentEls, signingEls);
          newParsed[form.id] = merged;

          const cbState: Record<string, boolean> = {};
          merged.checkboxes.forEach((c) => (cbState[c.identifier] = false));
          newCb[form.id] = cbState;
          newRadio[form.id] = {};
        });

        setParsedElements(newParsed);
        setCheckboxStates(newCb);
        setRadioSelections(newRadio);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to load consent forms:', err);
        if (cancelled) return;
        if (err.response?.status === 409) {
          setWorkflowAlreadyCompleted(true);
        } else {
          setError(getApiErrorMessage(err, 'Unable to load consent forms. Please try again.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workflowId, signerType]);

  /* -------------- Placeholder {{name}} -> <input> replacement ------ */

  const camelCaseToPlaceholder = useCallback((camelCase: string): string => {
    return camelCase
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .toLowerCase()
      .split(' ')
      .filter((w) => w.length > 0)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }, []);

  const replacePlaceholdersWithInputs = useCallback(
    (container: HTMLDivElement | null, formId: string, containerType: 'content' | 'signing') => {
      if (!container) return;
      const key = `${formId}_${containerType}`;
      if (processedPlaceholders.current[key]) return;

      const html = container.innerHTML;
      const regex = /\{\{(\w+)\}\}/g;
      let match;
      const replacements: Array<{ placeholder: string; name: string }> = [];
      while ((match = regex.exec(html)) !== null) {
        replacements.push({ placeholder: match[0], name: match[1] });
      }
      if (replacements.length === 0) {
        processedPlaceholders.current[key] = true;
        return;
      }

      let updatedHTML = html;
      replacements.forEach(({ placeholder, name }) => {
        if (name.toLowerCase() === 'signature') {
          updatedHTML = updatedHTML.replace(placeholder, '');
        } else if (name.startsWith('textarea_')) {
          const fieldName = name.slice('textarea_'.length);
          const label = camelCaseToPlaceholder(fieldName);
          updatedHTML = updatedHTML.replace(
            placeholder,
            `<textarea name="${fieldName}" placeholder="${label}" rows="4" class="border border-gray-300 rounded px-2 py-2 text-sm w-full focus:outline-none focus:border-[#01545D] transition-colors" style="display:block; margin-top:4px; box-sizing:border-box; resize:vertical;"></textarea>`,
          );
        } else {
          const label = camelCaseToPlaceholder(name);
          updatedHTML = updatedHTML.replace(
            placeholder,
            `<input type="text" name="${name}" placeholder="${label}" class="border-0 border-b-2 border-gray-300 bg-transparent px-2 py-2 text-sm focus:outline-none focus:border-[#01545D] transition-colors" style="min-width: min(200px, 100%); max-width: 100%; margin-bottom: 8px; display: block;" />`,
          );
        }
      });
      container.innerHTML = updatedHTML;
      processedPlaceholders.current[key] = true;
    },
    [camelCaseToPlaceholder],
  );

  /* -------------- Make checkboxes / radios in HTML interactive ------ */

  const makeFormElementsInteractive = useCallback(
    (container: HTMLDivElement | null, formId: string) => {
      if (!container) return;

      const cleanupKey = `${formId}_${
        container === contentRefs.current[formId] ? 'content' : 'signing'
      }`;
      if (eventListenerCleanups.current[cleanupKey]) {
        eventListenerCleanups.current[cleanupKey].forEach((fn) => fn());
        delete eventListenerCleanups.current[cleanupKey];
      }

      const cleanups: Array<() => void> = [];

      // Checkboxes
      container.querySelectorAll('input[type="checkbox"]').forEach((el) => {
        const cb = el as HTMLInputElement;
        const identifier = cb.getAttribute('name') || cb.getAttribute('id');
        if (!identifier) return;
        const cur = checkboxStates[formId]?.[identifier];
        if (cur === undefined) {
          setCheckboxStates((p) => ({
            ...p,
            [formId]: { ...(p[formId] || {}), [identifier]: cb.checked },
          }));
        } else {
          cb.checked = cur;
        }
        const handler = (e: Event) => {
          const t = e.target as HTMLInputElement;
          setCheckboxStates((p) => ({
            ...p,
            [formId]: { ...(p[formId] || {}), [identifier]: t.checked },
          }));
          setFormValidationErrors((p) => (p[formId]?.length ? { ...p, [formId]: [] } : p));
        };
        cb.addEventListener('change', handler);
        cleanups.push(() => cb.removeEventListener('change', handler));
      });

      // Radios
      container.querySelectorAll('input[type="radio"]').forEach((el) => {
        const radio = el as HTMLInputElement;
        const groupName = radio.getAttribute('name');
        if (!groupName) return;
        const value = radio.getAttribute('value') || '';
        const cur = radioSelections[formId]?.[groupName];
        if (cur === undefined && radio.checked) {
          setRadioSelections((p) => ({
            ...p,
            [formId]: { ...(p[formId] || {}), [groupName]: value },
          }));
        } else {
          radio.checked = cur === value;
        }
        const handler = (e: Event) => {
          const t = e.target as HTMLInputElement;
          if (t.checked) {
            setRadioSelections((p) => ({
              ...p,
              [formId]: { ...(p[formId] || {}), [groupName]: value },
            }));
            setFormValidationErrors((p) => (p[formId]?.length ? { ...p, [formId]: [] } : p));
          }
        };
        radio.addEventListener('change', handler);
        cleanups.push(() => radio.removeEventListener('change', handler));
      });

      eventListenerCleanups.current[cleanupKey] = cleanups;
    },
    [checkboxStates, radioSelections],
  );

  /* -------------- Run placeholder + interactive wiring on open form -- */

  useEffect(() => {
    if (!openForm) return;
    const form = consentForms.find((f) => f.id === openForm);
    if (!form) return;

    const cc = contentRefs.current[openForm];
    const sc = signingTextRefs.current[openForm];
    if (cc) replacePlaceholdersWithInputs(cc, openForm, 'content');
    if (sc) replacePlaceholdersWithInputs(sc, openForm, 'signing');

    setTimeout(() => {
      if (cc) makeFormElementsInteractive(cc, openForm);
      if (sc) makeFormElementsInteractive(sc, openForm);
    }, 0);
  }, [openForm, consentForms, signerType, replacePlaceholdersWithInputs, makeFormElementsInteractive]);

  // Reset processed placeholders when form is closed or signerType changes
  useEffect(() => {
    Object.keys(processedPlaceholders.current).forEach((key) => {
      if (key.endsWith('_signing')) delete processedPlaceholders.current[key];
    });
    consentForms.forEach((form) => {
      if (openForm !== form.id) {
        delete processedPlaceholders.current[`${form.id}_content`];
        delete processedPlaceholders.current[`${form.id}_signing`];
      }
    });
  }, [openForm, consentForms, signerType]);

  // Cleanup event listeners on close / unmount
  useEffect(() => {
    return () => {
      Object.values(eventListenerCleanups.current).forEach((arr) => arr.forEach((fn) => fn()));
      eventListenerCleanups.current = {};
    };
  }, []);

  useEffect(() => {
    consentForms.forEach((form) => {
      if (openForm !== form.id) {
        ['content', 'signing'].forEach((t) => {
          const k = `${form.id}_${t}`;
          if (eventListenerCleanups.current[k]) {
            eventListenerCleanups.current[k].forEach((fn) => fn());
            delete eventListenerCleanups.current[k];
          }
        });
      }
    });
  }, [openForm, consentForms]);

  /* -------------- Collect DOM state helpers ------------------------- */

  const getCheckboxStatesFromDOM = (formId: string): Record<string, boolean> => {
    const result: Record<string, boolean> = {};
    [contentRefs.current[formId], signingTextRefs.current[formId]].forEach((c) => {
      if (!c) return;
      c.querySelectorAll('input[type="checkbox"]').forEach((el) => {
        const input = el as HTMLInputElement;
        const id = input.getAttribute('name') || input.getAttribute('id');
        if (id) result[id] = input.checked;
      });
    });
    return result;
  };

  const getRadioSelectionsFromDOM = (formId: string): Record<string, string> => {
    const result: Record<string, string> = {};
    [contentRefs.current[formId], signingTextRefs.current[formId]].forEach((c) => {
      if (!c) return;
      c.querySelectorAll('input[type="radio"]').forEach((el) => {
        const input = el as HTMLInputElement;
        if (input.checked) {
          const name = input.getAttribute('name');
          if (name) result[name] = input.value ?? '';
        }
      });
    });
    return result;
  };

  /* -------------- Validation --------------------------------------- */

  const validateForm = useCallback(
    (formId: string) => {
      const parsed = parsedElements[formId];
      if (!parsed) return { isValid: true, errors: [] as string[] };

      const errors: string[] = [];
      const effCb = { ...(checkboxStates[formId] || {}), ...getCheckboxStatesFromDOM(formId) };
      const effRadio = { ...(radioSelections[formId] || {}), ...getRadioSelectionsFromDOM(formId) };

      parsed.mandatoryCheckboxes.forEach((id) => {
        if (!effCb[id]) {
          const label = parsed.fieldLabels.get(id) ?? id;
          errors.push(`Required checkbox "${label}" must be checked`);
        }
      });
      parsed.mandatoryRadioGroups.forEach((name) => {
        if (!effRadio[name]?.trim()) {
          const label = parsed.groupLabels.get(name) ?? name;
          errors.push(`Required field "${label}" must have a selection`);
        }
      });

      parsed.mandatoryCheckboxGroups.forEach((ids, groupName) => {
        const anyChecked = ids.some((id) => effCb[id] === true);
        if (!anyChecked) {
          const label = parsed.groupLabels.get(groupName) ?? groupName;
          errors.push(`At least one option must be selected for "${label}"`);
        }
      });

      return { isValid: errors.length === 0, errors };
    },
    [parsedElements, checkboxStates, radioSelections],
  );

  /* -------------- Collect placeholder data for submission ---------- */

  const collectPlaceholderData = (formId: string): Record<string, string | boolean> => {
    const data: Record<string, string | boolean> = {};

    // Text inputs
    [contentRefs.current[formId], signingTextRefs.current[formId]].forEach((c) => {
      if (!c) return;
      c.querySelectorAll('input[type="text"]').forEach((el) => {
        const input = el as HTMLInputElement;
        const name = input.name;
        const value = input.value.trim();
        if (name && value) data[name] = value;
      });
      // Collect textarea values with "textarea_" prefix
      c.querySelectorAll('textarea').forEach((el) => {
        const ta = el as HTMLTextAreaElement;
        const name = ta.getAttribute('name') || ta.getAttribute('id');
        if (name) data[`textarea_${name}`] = ta.value;
      });
    });
    

    // Checkboxes
    const effCb = { ...(checkboxStates[formId] || {}), ...getCheckboxStatesFromDOM(formId) };
    Object.entries(effCb).forEach(([id, checked]) => {
      data[`checkbox_${id}`] = checked;
    });

    // Radios
    const effRadio = { ...(radioSelections[formId] || {}), ...getRadioSelectionsFromDOM(formId) };
    Object.entries(effRadio).forEach(([name, value]) => {
      if (value?.trim()) data[`radio_${name}`] = value;
    });

    return data;
  };

  /* -------------- Sign a single form ------------------------------- */

  const handleSignForm = async (formId: string) => {
    if (!workflowId) {
      setError('Missing workflow id in URL.');
      return;
    }
    if (!UUID_REGEX.test(workflowId)) {
      setError('Invalid workflow id.');
      return;
    }

    const sigHandle = signatureRefs.current[formId];
    if (!sigHandle) {
      setError('Signature canvas not found. Please try again.');
      return;
    }
    const sigDataUrl = sigHandle.getSignature();
    if (!sigDataUrl) {
      setError('Please provide a signature before signing the form.');
      return;
    }

    const validation = validateForm(formId);
    if (!validation.isValid) {
      setFormValidationErrors((p) => ({ ...p, [formId]: validation.errors }));
      return;
    }
    setFormValidationErrors((p) => ({ ...p, [formId]: [] }));

    const base64Signature = sigDataUrl.replace(/^data:image\/png;base64,/, '');
    const placeholderData = collectPlaceholderData(formId);

    setSigningForms((p) => {
      const s = new Set(p);
      s.add(formId);
      return s;
    });
    setError(null);

    try {
      await axiosInstance.post(
        `/api/patient-consent/workflows/${encodeURIComponent(workflowId)}/sign`,
        {
          consentFormId: formId,
          signatureImage: base64Signature,
          signedByPatient: signerType === 'patient',
          placeholderData,
        },
      );

      setSignedForms((p) => {
        const s = new Set(p);
        s.add(formId);
        return s;
      });

      // Auto-collapse
      if (openForm === formId) setOpenForm(null);
    } catch (err: any) {
      // Current BE contract: 400 { errorCode, message } (no global popup on 400).
      const errorCode = getApiErrorCode(err);
      const errorMessage = getApiErrorMessage(err, '');
      const hasMissingPlaceholder =
        errorCode === 'MISSING_PLACEHOLDER_DATA' ||
        errorMessage.toLowerCase().includes('missing required placeholder');

      // Legacy BE: 500 with the reason in data.errors, which also raised the global
      // popup. Remove once the errorCode contract is deployed everywhere.
      const legacyErrors: string[] = err.response?.data?.errors || [];
      const hasLegacyMissingPlaceholder = legacyErrors.some((e: string) =>
        e.toLowerCase().includes('missing required placeholder'),
      );

      if (hasMissingPlaceholder || hasLegacyMissingPlaceholder) {
        // Only a legacy 500 raised the global popup; a 400 never does.
        if (err.response?.status !== 400) dispatch(hideError());
        setFormValidationErrors((p) => ({
          ...p,
          [formId]: ['Please fill all the fields in the form.'],
        }));
      } else if (err.response?.status === 400) {
        // Includes MISSING_REQUIRED_FIELDS: the message names the missing checkboxes/radios.
        const msg = errorMessage || err.response.data?.error || 'Validation failed';
        setFormValidationErrors((p) => ({ ...p, [formId]: [msg] }));
      } else if (err.response?.status === 409) {
        setError('Workflow state error. Please refresh and try again.');
      } else {
        console.error('Consent sign error', err, err?.response?.data);
        setError('Unable to sign the consent form. Please try again.');
      }
    } finally {
      setSigningForms((p) => {
        const s = new Set(p);
        s.delete(formId);
        return s;
      });
    }
  };

  /* -------------- Scroll-to-error helpers --------------------------- */

  const scrollToFormError = useCallback((formId: string) => {
    requestAnimationFrame(() => {
      errorAnchorRefs.current[formId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const scrollToPageTop = useCallback(() => {
    requestAnimationFrame(() => {
      pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  useEffect(() => {
    for (const [formId, errs] of Object.entries(formValidationErrors)) {
      if (errs && errs.length > 0) {
        scrollToFormError(formId);
        break;
      }
    }
  }, [formValidationErrors, scrollToFormError]);

  useEffect(() => {
    if (error) {
      scrollToPageTop();
    }
  }, [error, errorKey, scrollToPageTop]);

  /* -------------- Derived state ------------------------------------ */

  const allSigned = consentForms.length > 0 && consentForms.every((f) => signedForms.has(f.id));

  // Notify parent when all forms are signed
  useEffect(() => {
    if (allSigned && onComplete) {
      onComplete();
    }
  }, [allSigned, onComplete]);

  /* -------------- Render ------------------------------------------- */

  return (
    <>
        <div ref={pageTopRef} className="error-anchor" />
        <h1 className="text-4xl font-semibold text-[#01545D] text-center mb-3">
          Review &amp; Sign
        </h1>
        <div className="text-xl text-[#1C1C1C] text-center mb-6">
          Please review and sign all consent forms
        </div>

        {loading && (
          <div className="text-sm text-[#5D6A85] mb-2">Loading consent forms&hellip;</div>
        )}

        {error && (
          <div className="w-full max-w-[1200px] mb-2">
            <Alert type="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        {workflowAlreadyCompleted && (
          <div className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-4 sm:p-10 w-full max-w-[1200px] mt-4">
            <div className="bg-[#E0F2F3] border border-[#01545D] rounded-lg px-6 py-5 flex items-center gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#01545D] flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold text-[#01545D] mb-1">
                  Workflow already completed
                </div>
                <div className="text-sm text-[#1C1C1C]">
                  This consent workflow has already been completed. All forms have been signed.
                </div>
              </div>
            </div>
          </div>
        )}

        {!workflowAlreadyCompleted && (
        /* Main card */
        <div className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-4 sm:p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4">
          {/* Signer type toggle */}
          <section className="flex flex-col gap-3">
            <div className="text-base font-semibold text-[#1C1C1C]">
              Who is signing? <span className="text-red-500">*</span>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <button
                type="button"
                onClick={() => setSignerType('patient')}
                className={
                  'flex-1 py-3 px-5 rounded-md border text-base font-medium ' +
                  (signerType === 'patient'
                    ? 'border-[#01545D] bg-[#01545D] text-white'
                    : 'border-[#DCE1E8] bg-white text-[#1C1C1C]')
                }
              >
                I am patient
              </button>
              <button
                type="button"
                onClick={() => setSignerType('proxy')}
                className={
                  'flex-1 py-3 px-5 rounded-md border text-base font-medium ' +
                  (signerType === 'proxy'
                    ? 'border-[#01545D] bg-[#01545D] text-white'
                    : 'border-[#DCE1E8] bg-white text-[#1C1C1C]')
                }
              >
                I am signing on behalf of patient
              </button>
            </div>
          </section>

          {/* Consent form list */}
          <section className="flex flex-col gap-4">
            <div className="text-lg font-semibold text-[#01545D]">Consent Forms</div>

            {consentForms.length === 0 && !loading && (
              <div className="bg-[#E0F2F3] border border-[#01545D] rounded-lg px-6 py-5 flex items-center gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#01545D] flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-lg font-semibold text-[#01545D] mb-1">
                    No Consent Forms
                  </div>
                  <div className="text-sm text-[#1C1C1C]">
                    There are no consent forms to review at this time.
                  </div>
                </div>
              </div>
            )}

            {consentForms.map((form) => (
              <ConsentFormItem
                key={form.id}
                form={form}
                isOpen={openForm === form.id}
                isSigned={signedForms.has(form.id)}
                signerType={signerType}
                formValidationErrors={formValidationErrors[form.id]}
                signingForms={signingForms}
                onToggleOpen={() =>
                  setOpenForm(openForm === form.id ? null : form.id)
                }
                onSign={handleSignForm}
                contentRef={(el) => {
                  if (el) contentRefs.current[form.id] = el;
                }}
                signingTextRef={(el) => {
                  if (el) signingTextRefs.current[form.id] = el;
                }}
                signatureRef={(el) => {
                  if (el) signatureRefs.current[form.id] = el;
                }}
                errorAnchorRef={(el) => {
                  if (el) {
                    errorAnchorRefs.current[form.id] = el;
                  } else {
                    delete errorAnchorRefs.current[form.id];
                  }
                }}
              />
            ))}
          </section>

          {/* Info banner */}
          {!allSigned && consentForms.length > 0 && (
            <div className="flex items-start gap-3 bg-[#FFF3CD] border border-[#FFC107] rounded-lg px-4 py-3 mt-2">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#FFC107] text-white text-xs font-bold">
                !
              </div>
              <div className="text-sm text-[#856404]">
                Please sign all consent forms before proceeding
              </div>
            </div>
          )}

          {/* Success banner */}
          {allSigned && (
            <div className="bg-[#E0F2F3] border border-[#01545D] rounded-lg px-6 py-5 flex items-center gap-4 mt-2">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#01545D] flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold text-[#01545D] mb-1">
                  All Forms Signed
                </div>
                <div className="text-sm text-[#1C1C1C]">
                  Thank you! All consent forms have been signed successfully.
                </div>
              </div>
            </div>
          )}
        </div>
        )}
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Standalone page wrapper (backward-compatible route)                */
/* ------------------------------------------------------------------ */

const ConsentPage: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();

  if (!workflowId) {
    return (
      <div className="min-h-screen bg-[#F7F8FD] flex flex-col items-center justify-center">
        <div className="text-red-600">Missing workflow id in URL.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FD] flex flex-col">
      <div className="flex flex-row px-4 py-4 sm:px-10 sm:py-6 shadow justify-between bg-white items-center">
        <span className="text-[#01545D] font-semibold text-base sm:text-2xl">Patient Consent</span>
      </div>
      <main className="flex-grow container mx-auto flex flex-col items-center px-4 py-10">
        <ConsentPageContent workflowId={workflowId} />
      </main>
    </div>
  );
};

export default ConsentPage;
